import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { servicesData } from '../mock';
import { Milk, Award, Leaf, Stethoscope } from 'lucide-react';

const iconMap = {
  Milk: Milk,
  Horse: Award,
  Leaf: Leaf,
  Stethoscope: Stethoscope
};

export const Services = () => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, margin: '-100px' });

  return (
    <section ref={containerRef} className="py-32 bg-gradient-to-b from-farm-dark to-farm-brown/20">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <h2 className="text-5xl md:text-6xl font-serif font-bold text-farm-cream mb-6">
            Our <span className="text-farm-gold">Services</span>
          </h2>
          <p className="text-xl text-farm-cream/70 max-w-2xl mx-auto">
            Excellence in dairy farming and elite horse breeding
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          {servicesData.map((service, index) => {
            const Icon = iconMap[service.icon];
            return (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 50 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                className="group"
              >
                <div className="relative overflow-hidden rounded-3xl bg-farm-dark/50 backdrop-blur-sm border border-farm-green/20 hover:border-farm-gold/50 transition-all duration-500 shadow-2xl hover:shadow-farm-gold/20">
                  {/* Image */}
                  <div className="relative h-80 overflow-hidden">
                    <motion.img
                      whileHover={{ scale: 1.1 }}
                      transition={{ duration: 0.6 }}
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-farm-dark via-farm-dark/50 to-transparent" />

                    {/* Icon */}
                    <motion.div
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      className="absolute top-6 right-6 w-16 h-16 bg-farm-gold rounded-full flex items-center justify-center shadow-lg"
                    >
                      <Icon size={32} className="text-farm-dark" />
                    </motion.div>
                  </div>

                  {/* Content */}
                  <div className="p-8">
                    <h3 className="text-3xl font-serif font-bold text-farm-cream mb-4 group-hover:text-farm-gold transition-colors duration-300">
                      {service.title}
                    </h3>
                    <p className="text-farm-cream/70 mb-6 leading-relaxed">
                      {service.description}
                    </p>

                    {/* Features List */}
                    <div className="space-y-3">
                      {service.features.map((feature, idx) => (
                        <motion.div
                          key={idx}
                          initial={{ opacity: 0, x: -20 }}
                          animate={isInView ? { opacity: 1, x: 0 } : {}}
                          transition={{ duration: 0.5, delay: index * 0.2 + idx * 0.1 }}
                          className="flex items-center gap-3"
                        >
                          <div className="w-1.5 h-1.5 bg-farm-gold rounded-full" />
                          <span className="text-farm-cream/80">{feature}</span>
                        </motion.div>
                      ))}
                    </div>

                    {/* Learn More Button */}
                    {/* <motion.button
                      whileHover={{ x: 5 }}
                      className="mt-8 text-farm-gold font-semibold flex items-center gap-2 group-hover:gap-4 transition-all duration-300"
                    >
                      Learn More
                      <span>→</span>
                    </motion.button> */}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};